import { useState } from 'react';
import { Bar, BarChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { statsFacts } from '@/lib/statsFacts';
import { statsAskDemo } from '@/lib/statsAskDemo';
import { t, getLanguage } from '@/i18n';
import { Loader2, Sparkles } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

const SUGGESTED = ['ai.ask.q1', 'ai.ask.q2', 'ai.ask.q3', 'ai.ask.q4'];

const MiniChart = ({ chart }) => {
  const Chart = chart.type === 'line' ? LineChart : BarChart;
  return (
    <div>
      <div className="text-xs font-medium mb-1">{chart.title}</div>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <Chart data={chart.points} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={8} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            {chart.type === 'line'
              ? <Line dataKey="value" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
              : <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />}
          </Chart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// "Ask the statistics" (#71): a question in plain language, answered from the demo history.
export const StatsAsk = () => {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (text.length < 3) return;
    setQuestion(text);
    setBusy(true);
    const lang = getLanguage();
    const { mode, result } = await runAi('stats-ask', { question: text, lang, facts: statsFacts() }, statsAskDemo);
    setReply({ mode, result });
    setBusy(false);
  };

  const r = reply?.result;
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Sparkles className="h-4 w-4 text-primary" /> {t('ai.ask.title')} <HelpTip id="ask" />
      </div>
      <form className="flex gap-2" onSubmit={e => { e.preventDefault(); ask(); }}>
        <Input value={question} onChange={e => setQuestion(e.target.value)} placeholder={t('ai.ask.placeholder')} aria-label={t('ai.ask.title')} maxLength={300} />
        <Button type="submit" size="sm" className="h-9 shrink-0" disabled={busy || question.trim().length < 3}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('ai.ask.send')}
        </Button>
      </form>
      <div className="flex flex-wrap gap-1.5">
        {SUGGESTED.map(k => (
          <button key={k} type="button" onClick={() => ask(t(k))} className="text-[11px] rounded-full border px-2 py-0.5 text-muted-foreground hover:text-foreground text-left">
            {t(k)}
          </button>
        ))}
      </div>
      {r && (
        <div className="rounded-lg bg-muted/50 p-3 space-y-3" aria-live="polite">
          <div className="flex items-start gap-2">
            <p className="text-sm flex-1">{r.answer}</p>
            <AiBadge mode={reply.mode} />
          </div>
          {r.figures.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {r.figures.map(f => (
                <div key={f.label} className="rounded-md bg-card border p-2">
                  <div className="text-[11px] text-muted-foreground truncate" title={f.label}>{f.label}</div>
                  <div className="text-base font-bold tabular-nums">{f.value}</div>
                </div>
              ))}
            </div>
          )}
          {r.chart && <MiniChart chart={r.chart} />}
          <div className="text-[11px] text-muted-foreground">{t('ai.ask.source')}: {r.source}</div>
        </div>
      )}
    </Card>
  );
};
